/**
 * TappingPointsEditor — V2 port of TappingPointsTimestamp.
 *
 * Drops in 1:1 for the v1 component (same `items` shape, same
 * `handleTimestampChange(value, stateProp, index, field)` callback,
 * same `removeTimestamp(index)` and `onImportTimestamps` props),
 * but renders the prototype's `.tp-timeline` + `.tp-row` UI
 * (cms-redesign-mockup.html ~4534–4651 CSS, ~8489–8623 markup).
 *
 *   <TappingPointsEditor
 *     ref={timestampComponentRef}
 *     items={formData.session_bg_timestamps}
 *     tappingPoints={tapping_points}
 *     removeTimestamp={removeTimestamp}
 *     handleTimestampChange={handleTimestampChange}
 *     stateProp="session_bg_timestamps"
 *     onImportTimestamps={handleImportTimestamps}
 *     totalDurationSec={formData.session_length_in_sec}
 *     onAddTimestamp={addTimestamp}
 *   />
 *
 * Layout:
 *   • Header: counter + total audio length
 *   • Timeline: 6px sand track, teal progress bar to the LAST set
 *     marker, circular markers at each timestamp (hover shows the
 *     `LABEL · MM:SS` tooltip from `data-tp`)
 *   • Row list: index · point-pick (badge + name + caret, opens a
 *     dropdown to swap the tapping point) · time-input · remove
 *   • Action row: + Add point · Import · Clear all
 *
 * The point picker is a custom dropdown (not <select>) so we can
 * show the colored badge with each point's initials. Click-outside
 * + Escape close. Keyboard search is wired via a text input inside
 * the dropdown.
 *
 * Color cycle for badges: 5 brand tints, indexed by tapping_point_id
 * so the same point gets the same color across rows + the
 * waveform's marker layer.
 */
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import Swal from 'sweetalert2';
import ImportTimestampsDialogV2 from './ImportTimestampsDialogV2';
import {
  formatTimeToMMSS,
  parseMMSSToSeconds,
} from '../../../utils/timeFormat';

/* ─── Color palette + helpers ──────────────────────────────────── */

const BADGE_TINTS = [
  { bg: '#75A293', fg: '#fff' }, // teal-green
  { bg: '#E58A6A', fg: '#fff' }, // coral
  { bg: '#C4715B', fg: '#fff' }, // terracotta
  { bg: '#2C3E50', fg: '#fff' }, // navy
  { bg: '#A0B589', fg: '#fff' }, // sage
];

function badgeTint(id) {
  if (id == null) return BADGE_TINTS[0];
  const n = Math.abs(Number(id)) || 0;
  return BADGE_TINTS[n % BADGE_TINTS.length];
}

function tpInitials(name) {
  if (!name) return '··';
  const words = String(name).trim().split(/\s+/);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return words[0].slice(0, 2).toUpperCase();
}

function fmt(sec) {
  return formatTimeToMMSS(Number(sec) || 0);
}

/* ─── Point picker dropdown (single-row, opens on click) ──────── */

function PointPicker({
  value,
  catalog,
  onChange,
  excludedIds,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const selected = useMemo(() => {
    if (!value) return null;
    return (catalog || []).find(
      (c) => Number(c.tapping_point_id) === Number(value),
    );
  }, [value, catalog]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (catalog || [])
      .filter((c) => c && c.tapping_point_id && c.tapping_point_name)
      .filter((c) => !q || c.tapping_point_name.toLowerCase().includes(q));
  }, [catalog, query]);

  // Close on outside click + Escape
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    setTimeout(() => inputRef.current?.focus(), 30);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const tint = badgeTint(selected?.tapping_point_id);

  return (
    <div ref={wrapRef} style={{ position: 'relative', minWidth: 0 }}>
      {/* Trigger row — badge + name + caret. Whole row is the click
          target. */}
      <div
        onClick={() => setOpen((v) => !v)}
        className="flex items-center cursor-pointer"
        style={{
          gap: 10,
          padding: '4px 6px',
          borderRadius: 4,
          background: open ? '#FAF4EA' : 'transparent',
          transition: 'background 140ms ease',
          minWidth: 0,
        }}
        onMouseEnter={(e) => {
          if (!open) e.currentTarget.style.background = '#FAF4EA';
        }}
        onMouseLeave={(e) => {
          if (!open) e.currentTarget.style.background = 'transparent';
        }}
      >
        <div
          className="font-condensed font-bold grid place-items-center"
          style={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: selected ? tint.bg : '#E8E0CF',
            color: selected ? tint.fg : '#9CA1A8',
            fontSize: 10,
            letterSpacing: '0.02em',
            flexShrink: 0,
          }}
        >
          {selected ? tpInitials(selected.tapping_point_name) : '··'}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            className="text-navy"
            style={{
              fontSize: 13,
              fontWeight: 600,
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {selected ? selected.tapping_point_name : 'Pick a tapping point…'}
          </div>
          {selected && (
            <div
              className="font-condensed font-bold uppercase text-text-muted"
              style={{
                fontSize: 10.5,
                marginTop: 1,
                letterSpacing: '0.04em',
              }}
            >
              {tpInitials(selected.tapping_point_name)}
            </div>
          )}
        </div>
        <span
          className="text-text-muted"
          style={{
            fontSize: 10,
            marginLeft: 'auto',
            transition: 'transform 160ms ease',
            transform: open ? 'rotate(180deg)' : 'rotate(0)',
            flexShrink: 0,
          }}
        >
          ▾
        </span>
      </div>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            zIndex: 30,
            background: '#FAF7F1', /* surface */
            border: '1px solid #E8E0CF',
            borderRadius: 8,
            boxShadow: '0 10px 24px #2C3E5018, 0 2px 6px #2C3E500C',
            overflow: 'hidden',
            minWidth: 240,
          }}
        >
          <div style={{ padding: 8, borderBottom: '1px solid #ECE3D2' }}>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search tapping points…"
              style={{
                width: '100%',
                padding: '6px 8px',
                fontSize: 12,
                border: '1px solid #E8E0CF',
                borderRadius: 4,
                background: '#FAF4EA',
                outline: 'none',
                color: '#2C3E50',
              }}
            />
          </div>
          <div style={{ maxHeight: 240, overflowY: 'auto' }}>
            {filtered.length === 0 && (
              <div
                className="text-text-muted"
                style={{ padding: 12, fontSize: 12, textAlign: 'center' }}
              >
                No matches
              </div>
            )}
            {filtered.map((c) => {
              const id = c.tapping_point_id;
              const t = badgeTint(id);
              const isExcluded = excludedIds && excludedIds.has(Number(id));
              const isSelected = Number(value) === Number(id);
              return (
                <div
                  key={id}
                  onClick={() => {
                    onChange(c);
                    setOpen(false);
                    setQuery('');
                  }}
                  className="flex items-center cursor-pointer"
                  style={{
                    gap: 10,
                    padding: '8px 12px',
                    background: isSelected ? '#FAF4EA' : 'transparent',
                    opacity: isExcluded && !isSelected ? 0.5 : 1,
                    transition: 'background 120ms ease',
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) e.currentTarget.style.background = '#FAF4EA';
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <div
                    className="font-condensed font-bold grid place-items-center"
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      background: t.bg,
                      color: t.fg,
                      fontSize: 9,
                      flexShrink: 0,
                    }}
                  >
                    {tpInitials(c.tapping_point_name)}
                  </div>
                  <span
                    className="text-navy"
                    style={{
                      fontSize: 13,
                      flex: 1,
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {c.tapping_point_name}
                  </span>
                  {isExcluded && !isSelected && (
                    <span
                      className="font-mono text-text-muted"
                      style={{ fontSize: 10 }}
                      title="Already used in another row"
                    >
                      in use
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Time input (MM:SS) ───────────────────────────────────────── */

function TimeField({ value, onChange, max, inputRef, onTab }) {
  // Keep a local string so the user can type freely; commit on blur.
  const [local, setLocal] = useState(fmt(value));

  // Sync external changes when the row's seconds value changes
  // (e.g. via marker drag — not implemented yet — or import).
  useEffect(() => {
    setLocal(fmt(value));
  }, [value]);

  const commit = (raw) => {
    const sec = parseMMSSToSeconds(raw);
    if (Number.isNaN(sec) || sec < 0) return;
    const clamped = max != null ? Math.min(sec, max) : sec;
    onChange(clamped);
    setLocal(fmt(clamped));
  };

  return (
    <input
      ref={inputRef}
      className="font-mono"
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={(e) => commit(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit(e.currentTarget.value);
          e.currentTarget.blur();
        }
        if (e.key === 'Tab' && !e.shiftKey && onTab) {
          onTab(e);
        }
      }}
      placeholder="00:00"
      style={{
        width: '100%',
        padding: '6px 10px',
        fontSize: 13,
        border: '1px solid #E8E0CF',
        borderRadius: 4,
        background: '#FAF7F1',
        color: '#2C3E50',
        outline: 'none',
        textAlign: 'center',
      }}
    />
  );
}

/* ─── Timeline ───────────────────────────────────────────────────── */

function Timeline({ items, catalog, totalSec, currentSec, onSeek }) {
  const safeTotal = Number(totalSec) > 0 ? Number(totalSec) : 0;
  const progressPct = safeTotal > 0 ? Math.min(100, ((Number(currentSec) || 0) / safeTotal) * 100) : 0;

  // Track labels: quarter intervals
  const labels = useMemo(() => {
    if (safeTotal <= 0) return ['00:00', '', '', '', '00:00'];
    return [0, 0.25, 0.5, 0.75, 1].map((p) => fmt(p * safeTotal));
  }, [safeTotal]);

  return (
    <div
      style={{
        background: '#FAF4EA',
        border: '1px solid #E8E0CF',
        borderRadius: 8,
        padding: '28px 18px 14px',
        marginBottom: 14,
        position: 'relative',
      }}
    >
      <div
        className="font-condensed font-bold uppercase text-text-muted"
        style={{
          position: 'absolute',
          top: 8,
          left: 14,
          fontSize: 10,
          letterSpacing: '0.08em',
        }}
      >
        Timeline · 00:00 → {fmt(safeTotal)}
      </div>

      {/* Track */}
      <div
        style={{
          position: 'relative',
          height: 6,
          background: '#E8E0CF',
          borderRadius: 999,
          margin: '16px 0 10px',
        }}
      >
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: `${progressPct}%`,
            background: '#75A293',
            borderRadius: 999,
          }}
        />
        {items.map((it, i) => {
          const sec = Number(it.timestamp) || 0;
          if (safeTotal <= 0 || sec < 0) return null;
          const pct = Math.min(100, (sec / safeTotal) * 100);
          const catalogEntry = (catalog || []).find(
            (c) => Number(c.tapping_point_id) === Number(it.tapping_point_id),
          );
          const name = catalogEntry?.tapping_point_name || `Point ${i + 1}`;
          const initials = tpInitials(name);
          const inProgress = sec <= (currentSec || 0);
          const tint = badgeTint(it.tapping_point_id);
          return (
            <div
              key={`tp-mk-${i}`}
              data-tp={`${initials} · ${fmt(sec)}`}
              onClick={() => onSeek?.(sec)}
              style={{
                position: 'absolute',
                top: '50%',
                left: `${pct}%`,
                transform: 'translate(-50%, -50%)',
                width: 14,
                height: 14,
                borderRadius: '50%',
                background: inProgress ? '#75A293' : tint.bg,
                border: '2px solid #fff',
                boxShadow: '0 1px 3px #2C3E5040',
                cursor: 'pointer',
                transition: 'transform 180ms ease',
                zIndex: 1,
              }}
              title={`${name} · ${fmt(sec)}`}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1)';
              }}
            />
          );
        })}
      </div>

      {/* Track labels */}
      <div
        className="flex justify-between font-mono text-text-muted"
        style={{ fontSize: 10.5, padding: '0 1px' }}
      >
        {labels.map((l, i) => (
          <span key={i}>{l}</span>
        ))}
      </div>
    </div>
  );
}

/* ─── Main editor ─────────────────────────────────────────────── */

const TappingPointsEditor = forwardRef(function TappingPointsEditor(
  {
    items,
    catalog,
    tappingPoints, // alias for v1 compatibility
    removeTimestamp,
    handleTimestampChange,
    stateProp,
    onImportTimestamps,
    onAddTimestamp,
    onClearAll,
    totalDurationSec,
    currentSec,
    onSeek,
  },
  ref,
) {
  const resolvedCatalog = catalog || tappingPoints || [];
  const timeRefs = useRef([]);
  const [importOpen, setImportOpen] = useState(false);

  // Exposed for the parent's "Add timestamp" flow so it can focus
  // the newly-appended row's time input.
  useImperativeHandle(ref, () => ({
    focusOnNewItem: (index) => {
      setTimeout(() => {
        const el = timeRefs.current[index];
        if (el?.focus) {
          el.focus();
          if (el.select) el.select();
        }
      }, 60);
    },
  }));

  const safeItems = Array.isArray(items) ? items : [];
  const usedIds = useMemo(
    () => new Set(safeItems.map((it) => Number(it.tapping_point_id)).filter((n) => n)),
    [safeItems],
  );
  const filledCount = safeItems.filter((it) => it.tapping_point_id && Number(it.timestamp) >= 0).length;
  const totalCatalog = resolvedCatalog.length;

  const handleClearAll = async () => {
    if (safeItems.length === 0) return;
    const { isConfirmed } = await Swal.fire({
      title: 'Clear all tapping points?',
      text: 'This removes every row from the list. Saved data remains until you click Save.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Clear all',
      confirmButtonColor: '#C4715B',
    });
    if (!isConfirmed) return;
    if (onClearAll) {
      onClearAll();
    } else {
      // Fallback: remove from bottom to top so indices stay valid
      for (let i = safeItems.length - 1; i >= 0; i--) removeTimestamp(i);
    }
  };

  return (
    <div className="tp-section">
      {/* Header */}
      <div
        className="flex items-baseline justify-between"
        style={{ marginBottom: 12, gap: 16, flexWrap: 'wrap' }}
      >
        <span
          className="font-mono text-text-muted"
          style={{ fontSize: 12 }}
        >
          <b className="text-teal-d" style={{ fontWeight: 700 }}>
            {filledCount}
          </b>
          {totalCatalog > 0 ? ` / ${totalCatalog} tapping points` : ` markers`}
          {totalDurationSec ? (
            <>
              {' · audio length '}
              <b className="text-navy" style={{ fontWeight: 700 }}>
                {fmt(totalDurationSec)}
              </b>
            </>
          ) : null}
        </span>
      </div>

      {/* Timeline */}
      <Timeline
        items={safeItems}
        catalog={resolvedCatalog}
        totalSec={totalDurationSec}
        currentSec={currentSec}
        onSeek={onSeek}
      />

      {/* Row list */}
      <div>
        {safeItems.length === 0 && (
          <div
            className="text-text-muted"
            style={{
              padding: 16,
              fontSize: 13,
              textAlign: 'center',
              background: '#FAF7F1',
              border: '1px dashed #E8E0CF',
              borderRadius: 8,
            }}
          >
            No tapping points yet. Click <strong>+ Add point</strong> to start.
          </div>
        )}
        {safeItems.map((item, index) => {
          const tint = badgeTint(item.tapping_point_id);
          return (
            <div
              key={`tp-row-${index}`}
              className="tp-row"
              style={{
                display: 'grid',
                gridTemplateColumns: '28px 1fr 110px 32px',
                gap: 10,
                alignItems: 'center',
                padding: '8px 12px',
                background: '#FAF7F1',
                border: '1px solid #E8E0CF',
                borderRadius: 8,
                marginBottom: 6,
                transition: 'all 160ms ease',
                minWidth: 0,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#D6CCAE';
                e.currentTarget.style.boxShadow = '0 1px 3px #2C3E5012';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#E8E0CF';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div
                className="font-mono text-text-muted"
                style={{ fontSize: 11, textAlign: 'right' }}
              >
                {String(index + 1).padStart(2, '0')}
              </div>

              <PointPicker
                value={item.tapping_point_id}
                catalog={resolvedCatalog}
                excludedIds={usedIds}
                onChange={(c) => {
                  handleTimestampChange(
                    c.tapping_point_id,
                    stateProp,
                    index,
                    'tapping_point_id',
                  );
                  if (c.tapping_point_image_url) {
                    handleTimestampChange(
                      c.tapping_point_image_url,
                      stateProp,
                      index,
                      'image_url',
                    );
                  }
                }}
              />

              <TimeField
                value={item.timestamp}
                max={totalDurationSec}
                inputRef={(el) => (timeRefs.current[index] = el)}
                onChange={(sec) =>
                  handleTimestampChange(sec, stateProp, index, 'timestamp')
                }
              />

              <button
                type="button"
                onClick={() => removeTimestamp(index)}
                aria-label="Remove tapping point"
                className="grid place-items-center cursor-pointer"
                style={{
                  width: 28,
                  height: 28,
                  background: 'transparent',
                  border: '1px solid transparent',
                  borderRadius: '50%',
                  color: '#9CA1A8',
                  transition: 'all 140ms ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#D64545';
                  e.currentTarget.style.background = '#D6454514';
                  e.currentTarget.style.borderColor = '#D6454540';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '#9CA1A8';
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.borderColor = 'transparent';
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>

              {/* Hidden second-row debug: marker tint preview (kept
                  for visual consistency with the badge color cycle —
                  styling only, no extra state). */}
              <span style={{ display: 'none' }} aria-hidden>{tint.bg}</span>
            </div>
          );
        })}
      </div>

      {/* Action row */}
      <div
        className="flex flex-wrap"
        style={{ marginTop: 10, gap: 8 }}
      >
        <button
          type="button"
          onClick={onAddTimestamp}
          data-add-timestamp-button
          className="font-condensed font-bold uppercase cursor-pointer"
          style={{
            height: 32,
            padding: '0 14px',
            background: '#75A293',
            color: '#fff',
            border: 0,
            borderRadius: 6,
            fontSize: 12.5,
            letterSpacing: '0.06em',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <span style={{ fontSize: 16, lineHeight: 1 }}>+</span> Add point
        </button>
        <button
          type="button"
          onClick={() => setImportOpen(true)}
          className="font-condensed font-bold uppercase cursor-pointer inline-flex items-center"
          style={{
            height: 32,
            padding: '0 14px',
            background: '#FAF7F1',
            color: '#2C3E50',
            border: '1px solid #E8E0CF',
            borderRadius: 6,
            fontSize: 12.5,
            letterSpacing: '0.06em',
            gap: 6,
          }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
          Import from paste
        </button>
        {safeItems.length > 0 && (
          <button
            type="button"
            onClick={handleClearAll}
            className="font-condensed font-bold uppercase cursor-pointer inline-flex items-center"
            style={{
              height: 32,
              padding: '0 14px',
              background: 'transparent',
              color: '#C4715B',
              border: '1px solid transparent',
              borderRadius: 6,
              fontSize: 12.5,
              letterSpacing: '0.06em',
              gap: 6,
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            </svg>
            Clear all
          </button>
        )}
      </div>

      {/* V2 import dialog — uses the shared Modal primitive + brand
          chrome (cream textarea, brand badges for the catalog
          chips, cream/coral tinted result panels). Same parse +
          shape-the-result logic as the v1 dialog, so the parent's
          onImportTimestamps consumer stays unchanged. */}
      <ImportTimestampsDialogV2
        open={importOpen}
        onClose={() => setImportOpen(false)}
        tappingPoints={resolvedCatalog}
        onImport={(imported) => {
          if (onImportTimestamps) onImportTimestamps(imported);
          setImportOpen(false);
        }}
        maxDurationSeconds={totalDurationSec}
      />
    </div>
  );
});

export default TappingPointsEditor;
